// v1.0.0 - 2026-01-27 - GSE Corrispettivi sheet parser

import type * as XLSX from 'xlsx'
import type { GSERawCorrispettivi, ParseResult } from './gse-types'
import { GSE_COLUMN_MAPPINGS } from './gse-types'
import { sheetToJson, getRowValue, parseNumber, parseInt } from './xlsx-utils'
import { validateCorrispettivi } from './validators'

/**
 * Parse GSE Corrispettivi sheet
 *
 * Expected columns:
 * - Codice Contratto: CRIN format
 * - Anno: Year (2024, 2025, etc.)
 * - Mese: Month (1-12)
 * - TIP: Remunerazione energia condivisa (EUR)
 * - CUAfa: Valorizzazione componenti tariffarie (EUR)
 * - Costi GSE: Oneri amministrativi (EUR)
 * - EACI: Energia condivisa incentivabile (kWh)
 */
export function parseCorrispettiviSheet(
  sheet: XLSX.WorkSheet
): ParseResult<GSERawCorrispettivi[]> {
  const errors: string[] = []
  const warnings: string[] = []
  const data: GSERawCorrispettivi[] = []
  let skippedRows = 0

  // Convert sheet to JSON
  const rows = sheetToJson<Record<string, unknown>>(sheet)

  if (rows.length === 0) {
    return {
      success: false,
      data: null,
      errors: ['Foglio corrispettivi vuoto o formato non riconosciuto'],
      warnings: [],
      rowCount: 0,
      skippedRows: 0,
    }
  }

  const mapping = GSE_COLUMN_MAPPINGS.CORRISPETTIVI

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    const rowNum = i + 2 // Excel rows start at 1, plus header

    // Extract values using column mappings
    const codice = getRowValue<string>(row, mapping.codice_contratto)
    const annoRaw = getRowValue<string | number>(row, mapping.anno)
    const meseRaw = getRowValue<string | number>(row, mapping.mese)
    const tipRaw = getRowValue<string | number>(row, mapping.tip)
    const cuafaRaw = getRowValue<string | number>(row, mapping.cuafa)
    const costiRaw = getRowValue<string | number>(row, mapping.costi_gse)
    const eaciRaw = getRowValue<string | number>(row, mapping.eaci)

    // Skip empty rows
    if (!codice && !annoRaw && !meseRaw && !tipRaw) {
      skippedRows++
      continue
    }

    // Parse numeric values
    const anno = parseInt(annoRaw)
    const mese = parseInt(meseRaw)
    const tip = parseNumber(tipRaw)
    const cuafa = parseNumber(cuafaRaw)
    const costi = parseNumber(costiRaw)
    const eaci = parseNumber(eaciRaw)

    // Build record
    const record: Partial<GSERawCorrispettivi> = {
      codice_contratto: codice?.trim() || '',
      anno: anno ?? 0,
      mese: mese ?? 0,
      tip_eur: tip ?? 0,
      cuafa_eur: cuafa ?? 0,
      costi_gse_eur: costi ?? 0,
      eaci_kwh: eaci ?? 0,
      totale_lordo_eur: (tip ?? 0) + (cuafa ?? 0),
    }

    // Validate
    const validation = validateCorrispettivi(record)

    if (!validation.valid) {
      errors.push(`Riga ${rowNum}: ${validation.errors.join(', ')}`)
      skippedRows++
      continue
    }

    if (validation.warnings.length > 0) {
      warnings.push(`Riga ${rowNum}: ${validation.warnings.join(', ')}`)
    }

    data.push(record as GSERawCorrispettivi)
  }

  return {
    success: errors.length === 0 || data.length > 0,
    data: data.length > 0 ? data : null,
    errors,
    warnings,
    rowCount: rows.length,
    skippedRows,
  }
}

/**
 * Aggregate corrispettivi by period
 */
export function aggregateCorrispettiviByPeriod(
  data: GSERawCorrispettivi[]
): Map<string, GSERawCorrispettivi> {
  const result = new Map<string, GSERawCorrispettivi>()

  for (const record of data) {
    const key = `${record.anno}-${String(record.mese).padStart(2, '0')}`

    if (result.has(key)) {
      // Aggregate values (should not happen normally)
      const existing = result.get(key)!
      existing.tip_eur += record.tip_eur
      existing.cuafa_eur += record.cuafa_eur
      existing.costi_gse_eur += record.costi_gse_eur
      existing.eaci_kwh += record.eaci_kwh
      existing.totale_lordo_eur += record.totale_lordo_eur
    } else {
      result.set(key, { ...record })
    }
  }

  return result
}

/**
 * Aggregate corrispettivi for entire year
 */
export function aggregateCorrispettiviAnnual(
  data: GSERawCorrispettivi[]
): GSERawCorrispettivi | null {
  if (data.length === 0) return null

  const first = data[0]
  const result: GSERawCorrispettivi = {
    codice_contratto: first.codice_contratto,
    anno: first.anno,
    mese: 0, // Full year
    tip_eur: 0,
    cuafa_eur: 0,
    costi_gse_eur: 0,
    eaci_kwh: 0,
    totale_lordo_eur: 0,
  }

  for (const record of data) {
    result.tip_eur += record.tip_eur
    result.cuafa_eur += record.cuafa_eur
    result.costi_gse_eur += record.costi_gse_eur
    result.eaci_kwh += record.eaci_kwh
    result.totale_lordo_eur += record.totale_lordo_eur
  }

  return result
}

/**
 * Get corrispettivi summary statistics
 */
export function getCorrispettiviStats(data: GSERawCorrispettivi[]): {
  months: number
  total_tip_eur: number
  total_cuafa_eur: number
  total_costi_eur: number
  total_eaci_kwh: number
  avg_tip_per_kwh: number
} {
  let tip = 0
  let cuafa = 0
  let costi = 0
  let eaci = 0

  for (const record of data) {
    tip += record.tip_eur
    cuafa += record.cuafa_eur
    costi += record.costi_gse_eur
    eaci += record.eaci_kwh
  }

  return {
    months: data.length,
    total_tip_eur: tip,
    total_cuafa_eur: cuafa,
    total_costi_eur: costi,
    total_eaci_kwh: eaci,
    avg_tip_per_kwh: eaci > 0 ? tip / eaci : 0,
  }
}

/**
 * Extract codice contratto from corrispettivi
 */
export function extractCodiceContratto(data: GSERawCorrispettivi[]): string | null {
  if (data.length === 0) return null

  const codes = new Set(data.map((r) => r.codice_contratto))
  if (codes.size > 1) {
    console.warn('Multiple codici contratto found:', [...codes])
  }

  return data[0].codice_contratto
}
